import React from 'react'
import { Box } from 'components/Layout'
import {
  useEditCompanyPrice,
  useAddCompanyPrice,
  usePriceAddForm,
  PriceFieldName,
  getErrorMessage,
  useProducts,
  GetPrice_getCompanyPrice,
  SubLocation,
} from '@modmd/data'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import { SelectInput } from 'components/SelectInput'
import { useToaster } from 'utils/useToaster'
import { isNull, isUndefined } from 'lodash'
import { Sublocation } from 'sharedComponents/Sublocation/Sublocation'
import { getProducts } from '../../FinancesList/utils'

export interface CreateCompanyProps {
  companyPriceItem: GetPrice_getCompanyPrice | undefined
  companyId: string
  groupId?: string
  isAdding: boolean
  onSubmit: () => void
}

const CreateEditPrices = ({
  companyPriceItem,
  companyId,
  groupId,
  isAdding,
  onSubmit,
}: CreateCompanyProps) => {
  const { setToastMessage } = useToaster()

  const { products, isProductsDataLoading } = useProducts()

  const productOptions = React.useMemo(() => (products ? getProducts(products) : []), [products])

  const [EditPrice, { loading: isEditLoading }] = useEditCompanyPrice({
    onCompleted: () => {
      setToastMessage('Data successfully changed', 'success')
    },
  })
  const [addPrice, { loading: isAddLoading }] = useAddCompanyPrice({
    onCompleted: () => {
      setToastMessage('Data successfully added', 'success')
    },
  })

  const priceForm = usePriceAddForm({
    validationSchema: 'fullPrice',
    initialValues: {
      productId: companyPriceItem?.productId,
      price: companyPriceItem?.price,
      minQuantity: companyPriceItem?.minQuantity,
      sublocation: companyPriceItem?.sublocation || SubLocation.Lab,
    },
    onSubmit: () => {},
  })

  const submitEditForm = async () => {
    if (!isNull(companyPriceItem?.id) && priceForm.values.productId) {
      try {
        await EditPrice({
          variables: {
            inputData: {
              id: !isNull(companyPriceItem?.id) ? String(companyPriceItem?.id) : '',
              companyId,
              ...(groupId && { groupId }),
              productId: priceForm.values.productId,
              price: priceForm.values.price || 0,
              minQuantity: priceForm.values.minQuantity || 0,
              sublocation: priceForm.values.sublocation as SubLocation,
            },
          },
        })
        onSubmit()
      } catch {
        // ignore
      }
    }
  }

  const submitAddForm = async () => {
    if (priceForm.values.productId) {
      try {
        await addPrice({
          variables: {
            inputData: {
              companyId,
              ...(groupId && { groupId }),
              productId: priceForm.values.productId,
              price: priceForm.values.price || 0,
              minQuantity: priceForm.values.minQuantity || 0,
              sublocation: priceForm.values.sublocation as SubLocation,
            },
          },
        })
        onSubmit()
      } catch {
        // ignore
      }
    }
  }

  return (
    <>
      <Box display="grid" marginTop="1rem">
        <TextInput
          name={PriceFieldName.PRICE}
          type="number"
          value={priceForm?.values?.price}
          onChange={priceForm?.handleChange}
          onBlur={priceForm?.handleBlur}
          errorMessage={getErrorMessage(priceForm?.touched.price, priceForm?.errors.price)}
          label="Price"
          placeholder="0.00"
          isFullWidth
        />
        <TextInput
          name={PriceFieldName.MIN_QUANTITY}
          type="number"
          value={priceForm?.values.minQuantity}
          onChange={priceForm?.handleChange}
          onBlur={priceForm?.handleBlur}
          errorMessage={getErrorMessage(
            priceForm?.touched.minQuantity,
            priceForm?.errors.minQuantity
          )}
          label="Min. Quantity"
          placeholder="0"
          isFullWidth
        />
        {!isProductsDataLoading && (
          <SelectInput
            name="productId"
            label="Product"
            value={priceForm?.values.productId || ''}
            onChange={priceForm?.handleChange}
            options={[{ label: 'Select an item', value: '', disabled: true }, ...productOptions]}
          />
        )}
        <Sublocation
          sublocation={priceForm?.values?.sublocation || ''}
          refetch={() => {}}
          onChange={priceForm?.handleChange}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Button
          disabled={
            !priceForm.isValid ||
            (isUndefined(priceForm?.values?.price) && isUndefined(priceForm?.values?.minQuantity))
          }
          isFetching={isAddLoading || isEditLoading}
          onClick={isAdding ? submitAddForm : submitEditForm}
        >
          Save
        </Button>
      </Box>
    </>
  )
}

export { CreateEditPrices }
